<template>
  <div
    id="viewer"
    class="image-reader"
  />
</template>

<script>
/**
 * ImageViewer component for displaying images with OCR overlay
 * Handles canvas-based polygon overlays and zoom functionality
 */
export default {
    name: 'ImageViewer',
    props: {
        imgFile: {
            type: Blob,
            required: true
        },
        parsedLines: {
            type: Array,
            default: () => []
        },
        pageGroupedLines: {
            type: Object,
            default: () => ({})
        },
        disableHoverEffects: {
            type: Boolean,
            default: false
        },
        activePolygonIndices: {
            type: Array,
            default: null
        }
    },
    emits: ['imageLoaded', 'polygonClicked'],
    data() {
        return {
            imageZoom: 1,
            imgElement: null,
            canvasesByPage: {},
            currentHoveredPolygonIdx: null,
            localPageGroupedLines: {},
        };
    },

    watch: {
        imgFile: {
            handler() {
                this.$nextTick(() => {
                    this.displayImage();
                });
            },
            immediate: false
        },
        parsedLines: {
            handler(newParsedLines) {
                if (newParsedLines && newParsedLines.length > 0 && this.imgElement) {
                    console.log("Parsed lines updated for image, initializing canvas overlay");
                    this.$nextTick(() => {
                        this.initializeCanvasOverlayForImage(this.imgElement);
                    });
                }
            },
            deep: true
        },
        disableHoverEffects(newVal) {
            // Disable pointer events on canvas when a dialog is open
            this.updateCanvasPointerEvents();
        },
        activePolygonIndices() {
            const canvas = this.canvasesByPage[0];
            if (canvas) {
                this.drawPolygonsOnCanvasForPage(0, canvas);
            }
        }
    },

    mounted() {
        if (this.imgFile) {
            this.displayImage();
        }
    },

    beforeUnmount() {
        this.dispose();
    },

    methods: {
        displayImage() {
            const element = document.getElementById('viewer');
            element.innerHTML = '';
            const imgURL = URL.createObjectURL(this.imgFile);

            // Set viewer styles for vertical scrolling and centering
            element.style.display = 'flex';
            element.style.justifyContent = 'center';
            element.style.alignItems = 'flex-start';
            element.style.overflowY = 'auto';
            element.style.overflowX = 'hidden';
            element.style.height = '100%';
            element.style.paddingTop = '20px';

            // Create wrapper for image and canvas overlay
            const wrapperContainer = document.createElement('div');
            wrapperContainer.id = 'imageWrapper';
            wrapperContainer.style.position = 'relative';
            wrapperContainer.style.display = 'inline-block';
            wrapperContainer.style.width = 'fit-content';
            wrapperContainer.style.height = 'fit-content';

            const imgElement = document.createElement('img');
            imgElement.id = 'imageResource';
            imgElement.src = imgURL;
            imgElement.style.display = 'block';
            imgElement.style.transform = `scale(${this.imageZoom})`;
            imgElement.style.transformOrigin = 'top center';
            imgElement.style.transition = 'transform 0.2s ease';

            this.imgElement = imgElement;

            wrapperContainer.appendChild(imgElement);
            element.appendChild(wrapperContainer);

            imgElement.onload = () => {
                this.$nextTick(() => {
                    if (this.parsedLines.length > 0) {
                        this.initializeCanvasOverlayForImage(imgElement);
                    }
                    this.setupImageZoomListener();
                    this.$emit('imageLoaded');
                });
            };
        },

        initializeCanvasOverlayForImage(imgElement) {
            this.canvasesByPage = {};
            document.querySelectorAll('canvas[id^="ocr-canvas-"]').forEach(canvas => {
                canvas.remove();
            });

            const wrapperContainer = document.getElementById('imageWrapper');
            if (!wrapperContainer) {
                return;
            }

            const page = 0;

            // Create local copy of pageGroupedLines if not already done
            if (Object.keys(this.localPageGroupedLines).length === 0) {
                if (!this.pageGroupedLines[page]) {
                    console.warn(`Page ${page} not found in pageGroupedLines, using all parsed lines`);
                    this.localPageGroupedLines[page] = this.parsedLines.filter(line => {
                        return line.page === 0 || line.page === undefined || line.page === null;
                    });

                    if (this.localPageGroupedLines[page].length === 0) {
                        console.log("No page 0 lines found, using all parsed lines for image");
                        this.localPageGroupedLines[page] = this.parsedLines;
                    }
                } else {
                    this.localPageGroupedLines = JSON.parse(JSON.stringify(this.pageGroupedLines));
                }
            }

            // Create canvas for image overlay
            const canvas = document.createElement('canvas');
            canvas.id = `ocr-canvas-${page}`;
            canvas.className = 'ocr-canvas';
            canvas.style.position = 'absolute';
            canvas.style.top = '0';
            canvas.style.left = '0';
            canvas.style.zIndex = '100';
            canvas.style.cursor = 'pointer';
            canvas.style.pointerEvents = 'auto';
            canvas.style.transform = `scale(${this.imageZoom})`;
            canvas.style.transformOrigin = 'top center';
            canvas.style.transition = 'transform 0.2s ease';

            const canvasWidth = imgElement.naturalWidth;
            const canvasHeight = imgElement.naturalHeight;

            if (!canvasWidth || !canvasHeight) {
                return;
            }

            canvas.width = canvasWidth;
            canvas.height = canvasHeight;

            wrapperContainer.appendChild(canvas);
            this.canvasesByPage[page] = canvas;

            this.drawPolygonsOnCanvasForPage(page, canvas);

            canvas.addEventListener('mousemove', (e) => this.onCanvasMouseMove(e, page, canvas));
            canvas.addEventListener('click', (e) => this.onCanvasClick(e, page, canvas));
        },

        drawPolygonsOnCanvasForPage(page, canvas) {
            if (!canvas) return;

            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const polygons = this.localPageGroupedLines[page] || this.pageGroupedLines[page] || [];

            polygons.forEach((polygon, idx) => {
                if (polygon.points && polygon.points.length > 0) {
                    this.drawPolygonOnContext(
                        polygon,
                        idx === this.currentHoveredPolygonIdx,
                        ctx,
                        canvas,
                        true,  // isImageResource flag
                        this.activePolygonIndices ? this.activePolygonIndices.includes(idx) : false
                    );
                }
            });
        },

        drawPolygonOnContext(polygon, isHovered = false, ctx, canvas, isImageResource = false, isVoiceActive = false) {
            if (!ctx || !polygon.points || polygon.points.length < 3) return;

            const points = polygon.points;
            const imageBbox = polygon.imageBbox || [0, 0, 560, 794];
            const bboxX = imageBbox[0];
            const bboxY = imageBbox[1];

            let scaleX = 1.0;
            let scaleY = 1.0;

            if (!isImageResource) {
                const originalWidth = imageBbox[2] - imageBbox[0];
                const originalHeight = imageBbox[3] - imageBbox[1];
                scaleX = canvas.width / originalWidth;
                scaleY = canvas.height / originalHeight;
            }

            ctx.strokeStyle = isVoiceActive ? 'rgba(255, 210, 0, 0.95)' : isHovered ? 'rgb(255, 165, 0)' : 'rgba(0, 0, 0, 0)';
            ctx.fillStyle = isVoiceActive ? 'rgba(255, 230, 0, 0.35)' : isHovered ? 'rgba(255, 200, 0, 0.2)' : 'rgba(0, 0, 0, 0)';
            ctx.lineWidth = isVoiceActive || isHovered ? 2 : 1;

            ctx.beginPath();
            ctx.moveTo((points[0].x - bboxX) * scaleX, (points[0].y - bboxY) * scaleY);
            for (let i = 1; i < points.length; i++) {
                ctx.lineTo((points[i].x - bboxX) * scaleX, (points[i].y - bboxY) * scaleY);
            }
            ctx.closePath();

            ctx.fill();
            ctx.stroke();
        },

        isPointInPolygon(x, y, polygon, canvas, isImageResource = false) {
            const points = polygon.points || [];
            const imageBbox = polygon.imageBbox || [0, 0, 560, 794];
            const bboxX = imageBbox[0];
            const bboxY = imageBbox[1];

            let scaleX = 1.0;
            let scaleY = 1.0;

            if (!isImageResource) {
                const originalWidth = imageBbox[2] - imageBbox[0];
                const originalHeight = imageBbox[3] - imageBbox[1];
                const canvasWidth = canvas ? canvas.width : 425;
                const canvasHeight = canvas ? canvas.height : 604;
                scaleX = canvasWidth / originalWidth;
                scaleY = canvasHeight / originalHeight;
            }

            let inside = false;
            for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
                const xi = (points[i].x - bboxX) * scaleX;
                const yi = (points[i].y - bboxY) * scaleY;
                const xj = (points[j].x - bboxX) * scaleX;
                const yj = (points[j].y - bboxY) * scaleY;

                const intersect = ((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi);
                if (intersect) inside = !inside;
            }
            return inside;
        },

        onCanvasMouseMove(event, page, canvas) {
            // Skip hover effects if they are disabled (e.g., when a dialog is open)
            if (this.disableHoverEffects) {
                return;
            }

            if (!canvas) return;

            const rect = canvas.getBoundingClientRect();
            const x = (event.clientX - rect.left) / this.imageZoom;
            const y = (event.clientY - rect.top) / this.imageZoom;

            const polygons = this.localPageGroupedLines[page] || this.pageGroupedLines[page] || [];
            let hoveredIdx = null;

            for (let i = 0; i < polygons.length; i++) {
                const polygon = polygons[i];
                if (polygon.points && this.isPointInPolygon(x, y, polygon, canvas, true)) {
                    hoveredIdx = i;
                    break;
                }
            }

            if (hoveredIdx !== this.currentHoveredPolygonIdx) {
                this.currentHoveredPolygonIdx = hoveredIdx;
                this.drawPolygonsOnCanvasForPage(page, canvas);
            }
        },

        onCanvasClick(event, page, canvas) {
            // Skip click events if they are disabled (e.g., when a dialog is open)
            if (this.disableHoverEffects) {
                return;
            }

            if (!canvas) return;

            const rect = canvas.getBoundingClientRect();
            const x = (event.clientX - rect.left) / this.imageZoom;
            const y = (event.clientY - rect.top) / this.imageZoom;

            const polygons = this.localPageGroupedLines[page] || this.pageGroupedLines[page] || [];

            for (let i = 0; i < polygons.length; i++) {
                const polygon = polygons[i];
                if (polygon.points && this.isPointInPolygon(x, y, polygon, canvas, true)) {
                    console.log(`Clicked on polygon: "${polygon.text}"`);
                    this.$emit('polygonClicked', {
                        polygon,
                        page,
                        index: i
                    });
                    break;
                }
            }
        },

        setupImageZoomListener() {
            const zoomHandler = (e) => {
                if (e.key === '+' || e.key === '=') {
                    e.preventDefault();
                    this.imageZoom = Math.min(this.imageZoom + 0.1, 3);
                    this.updateImageZoom();
                } else if (e.key === '-' || e.key === '_') {
                    e.preventDefault();
                    this.imageZoom = Math.max(this.imageZoom - 0.1, 0.5);
                    this.updateImageZoom();
                } else if (e.key === '0') {
                    e.preventDefault();
                    this.imageZoom = 1;
                    this.updateImageZoom();
                }
            };

            const wheelHandler = (e) => {
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    const delta = e.deltaY > 0 ? -0.1 : 0.1;
                    this.imageZoom = Math.max(0.5, Math.min(3, this.imageZoom + delta));
                    this.updateImageZoom();
                }
            };

            window.addEventListener('keydown', zoomHandler);
            const viewerElement = document.getElementById('viewer');
            if (viewerElement) {
                viewerElement.addEventListener('wheel', wheelHandler, { passive: false });
            }
        },

        updateImageZoom() {
            if (this.imgElement) {
                this.imgElement.style.transform = `scale(${this.imageZoom})`;

                const canvas = this.canvasesByPage[0];
                if (canvas) {
                    canvas.style.transform = `scale(${this.imageZoom})`;
                }
            }
        },

        updateCanvasPointerEvents() {
            // Update pointer events on all canvases based on disableHoverEffects
            const canvases = document.querySelectorAll('canvas[id^="ocr-canvas-"]');
            canvases.forEach(canvas => {
                if (this.disableHoverEffects) {
                    canvas.style.pointerEvents = 'none';
                } else {
                    canvas.style.pointerEvents = 'auto';
                }
            });
        },

        dispose() {
            if (this.imgElement && this.imgElement.src) {
                URL.revokeObjectURL(this.imgElement.src);
                this.imgElement = null;
            }
        }
    }
};
</script>

<style scoped>
.image-reader {
    width: 100%;
    height: 100%;
    position: relative;
}
</style>
